import { ChangeDetectorRef, Component, NgZone, OnInit, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { finalize } from 'rxjs/operators';
import {Table, TableLazyLoadEvent, TableModule} from 'primeng/table';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import {Expense} from "../expense.model";
import {ExpenseService} from "../expense.service";
import {AccountsService} from "../../accounts/accounts.service";
import {ChartOfAccountsService} from "../../chart-of-accounts/chart-of-accounts.service";
import {ChartOfAccounts} from "../../chart-of-accounts/chart-of-accounts.model";
import {APIListWrapper} from "../../models/common/api-list-wrapper.model";
import { JournalEntriesService } from 'src/app/accounts/journal-entries/journal-entries.service';
import {forkJoin } from "rxjs";
import {Journal} from "../../accounts/journal-entries/models/journal.model";
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { app_permissions } from '../../security/app-permissions';
import { PermissionService } from '../../security/permission.service';

// PrimeNG Modules
import { ToastModule } from 'primeng/toast';
import { ButtonModule } from 'primeng/button';
import { ToolbarModule } from 'primeng/toolbar';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { DatePickerModule } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { IftaLabelModule } from "primeng/iftalabel";


@Component({
    selector: 'app-expense-posting',
    templateUrl: './expense-posting.component.html',
    standalone: true,
    imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    ToastModule,
    ButtonModule,
    TableModule,
    ToolbarModule,
    BreadcrumbModule,
    InputTextModule,
    DialogModule,
    ConfirmDialogModule,
    InputNumberModule,
    SelectModule,
    DatePickerModule,
    InputGroupModule,
    InputGroupAddonModule,
    IftaLabelModule
],
    providers: [
        MessageService,
        ExpenseService,
        AccountsService,
        ChartOfAccountsService,
        JournalEntriesService
    ]
})
export class ExpensePostingComponent implements OnInit {
    private readonly ngZone = inject(NgZone);
    private readonly cdr = inject(ChangeDetectorRef);
    @ViewChild('dt') expensesToPostTable: Table; // Reference to the table for resetting

    isLoading = false;
    approvedExpenses: Expense[] = [];
    selectedExpenses: Expense[] = [];
    totalRecords = 0; // For table pagination
    canPostExpense = false;

    // Dialog and Form
    displayPostingDialog = false;
    postingForm: FormGroup;
    totalToPost = 0;

    // Data for forms
    expenseJournal: Journal | undefined;
    expenseAccounts: ChartOfAccounts[] = [];
    liabilityAccounts: ChartOfAccounts[] = [];

    private owningCompanyId: number;

    constructor(
        private expenseService: ExpenseService,
        private accountsService: AccountsService,
        private chartOfAccountsService: ChartOfAccountsService,
        private journalEntriesService: JournalEntriesService, // Add this service
        private sessionStorageService: SessionStorageService,
        private permissionService: PermissionService,
        private fb: FormBuilder,
        private messageService: MessageService
    ) {
        this.owningCompanyId = this.sessionStorageService.getCompany()?.id ?? 0;
    }

    ngOnInit(): void {
        this.canPostExpense = this.permissionService.hasPermission(app_permissions.expense.posting.post);
        this.loadDropdownData(); // Load non-paginated data once
        this.buildForm();
    }

    private syncView(work?: () => void): void {
        this.ngZone.run(() => {
            work?.();
            this.cdr.detectChanges();
        });
    }

    /**
     * Lazily loads expenses based on table pagination, sorting, and filtering.
     * This is triggered by the (onLazyLoad) event from the p-table.
     */
    loadExpensesLazy(event: TableLazyLoadEvent): void {
        this.isLoading = true;
        const page = event.first! / event.rows!;
        const pageSize = event.rows!;

        this.expenseService.getFullyApprovedExpenses(this.owningCompanyId, page, pageSize)
            .pipe(finalize(() => this.syncView(() => this.isLoading = false)))
            .subscribe({
                next: (response: APIListWrapper<Expense>) => {
                    this.syncView(() => {
                        this.approvedExpenses = response.items;
                        this.totalRecords = response.count;
                    });
                },
                error: (err) => {
                    console.error('Failed to load approved expenses:', err);
                    this.syncView(() => {
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load expenses.' });
                    });
                }
            });
    }

    /**
     * Loads the full chart of accounts and filters it client-side for the dropdowns.
     * This is called only once when the component initializes.
     */
    loadDropdownData(): void {
        this.chartOfAccountsService.getAllChartOfAccountsByCompany(this.owningCompanyId, false)
            .subscribe({
                next: (allAccounts) => {
                    this.syncView(() => {
                        this.expenseAccounts = allAccounts
                            .filter(acc => this.searchChartOfAccountsWithChildren(acc, 'Expense'));
                        const creditAccounts = new Map<number, ChartOfAccounts>();
                        const parentIds = new Set(allAccounts.map(account => account.parentAccount?.accountId).filter((id): id is number => !!id));
                        const collectAccounts = (accounts: ChartOfAccounts[]) => accounts.forEach(account => {
                            if (account.childrenAccounts?.length) collectAccounts(account.childrenAccounts);
                            else if (account.accountId && !parentIds.has(account.accountId)
                                && account.isActive && !account.deprecated) {
                                creditAccounts.set(account.accountId, account);
                            }
                        });
                        collectAccounts(allAccounts);
                        this.liabilityAccounts = Array.from(creditAccounts.values())
                            .filter(account => account.accountType?.code?.toUpperCase() === 'AP');
                    });
                },
                error: (err) => {
                    console.error('Failed to load Chart of Accounts:', err);
                    this.syncView(() => {
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load account data.' });
                    });
                }
            });
        this.journalEntriesService.getAllJournalsByCompany(this.owningCompanyId).subscribe({
            next: value => this.syncView(() => {
                this.expenseJournal = value.find(value1 => value1.journalName.toLowerCase().includes('expense'));
            })
        });
    }

    searchChartOfAccountsWithChildren(account: ChartOfAccounts, searchText: string): boolean {
        if (account.childrenAccounts && account.childrenAccounts.length > 0) {
            return account.childrenAccounts.some(child => this.searchChartOfAccountsWithChildren(child, searchText));
        } else {
            return account.accountName?.toLowerCase().includes(searchText.toLowerCase());
        }
    }

    buildForm(): void {
        this.postingForm = this.fb.group({
            voucherDate: [new Date(), Validators.required],
            debitAccountId: [null, Validators.required],
            creditAccountId: [null],
            notes: [''],
        });
    }

    openPostingDialog(): void {
        if (this.selectedExpenses.length === 0) return;

        this.totalToPost = this.selectedExpenses.reduce((sum, exp) => sum + (exp.totalAmount ?? 0), 0);
        this.postingForm.patchValue({
            creditAccountId: null,
            voucherDate: new Date(),
            debitAccountId: null, // Reset debit account on open
            notes: ''
        });

        this.displayPostingDialog = true;
    }

    postVoucherAndJournalEntry() {
        if (!this.canPostExpense) return;
        if (this.postingForm.invalid) {
            this.messageService.add({ severity: 'warn', summary: 'Validation Error', detail: 'Please fill all required fields.' });
            return;
        }

        const formValue = this.postingForm.value;

        if (!formValue.debitAccountId || this.selectedExpenses.some(expense =>
            !formValue.creditAccountId && !expense.creditAccountId)) {
            this.messageService.add({ severity: 'error', summary: 'Data Error', detail: 'Select a debit account and provide a credit account for every expense.' });
            this.isLoading = false;
            return;
        }

        this.isLoading = true;

        const postingObservables = this.selectedExpenses.map(expense =>
            this.expenseService.postExpenseToJournal(expense.expenseId!, formValue.debitAccountId,
                formValue.creditAccountId || expense.creditAccountId!)
        );

        forkJoin(postingObservables).pipe(
            finalize(() => this.syncView(() => this.isLoading = false))
        ).subscribe({
            next: (results) => {
                this.syncView(() => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: `${results.length} expense(s) have been successfully posted to the General Ledger.`
                    });
                    this.displayPostingDialog = false;
                    this.selectedExpenses = [];
                });
                this.expensesToPostTable.reset(); // Reset the table to fetch the updated list.
            },
            error: (err) => {
                console.error('Failed to post expenses:', err);
                this.syncView(() => {
                    this.displayPostingDialog = false;
                    this.selectedExpenses = [];
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Posting Failed',
                        detail: err.message || 'An error occurred while posting. Not all expenses may have been processed.'
                    });
                });
                this.expensesToPostTable.reset();
            }
        });
    }

    canConfirmPosting(): boolean {
        return this.canPostExpense && !!this.postingForm?.valid
            && this.selectedExpenses.length > 0
            && this.selectedExpenses.every(expense =>
                !!this.postingForm.get('creditAccountId')?.value || !!expense.creditAccountId);
    }

}
